// src/components/RentListing.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, doc, updateDoc, arrayUnion, increment, getDocs } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  FaMapMarkerAlt, FaMedal, FaStar, FaUserFriends, FaBed, FaCheckCircle,
  FaFilter, FaEye, FaHandPointer, FaBuilding
} from 'react-icons/fa';
import { Gem, ShieldCheck, Compass } from "lucide-react";
import { db, storage } from './firebase';
import './PGListing.css';
import apnagharLogo from './apnaghar.png';
import { FaShieldAlt, FaUserCheck } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// Loading Screen
function LoadingScreen({ loading }) {
  if (!loading) return null;
  return (
    <div className="loading-screen">
      <div className="loader"></div>
      <p>Please wait, we are getting your rentals ready...</p>
    </div>
  );
}

// Filter Dropdown
function FilterDropdown({ filters, setFilters, onApply, onReset, onClose }) {
  return (
    <div className="filter-dropdown">
      <button className="filter-close-btn" onClick={onClose}>×</button>
      <div className="filter-group">
        <label>Price Range (₹/month)</label>
        <div className="price-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.minRent}
            onChange={(e) => setFilters(f => ({ ...f, minRent: e.target.value }))}
          />
          <span>–</span>
          <input
            type="number"
            placeholder="Max"
            value={filters.maxRent}
            onChange={(e) => setFilters(f => ({ ...f, maxRent: e.target.value }))}
          />
        </div>
      </div>
      <div className="filter-group">
        <label>Occupancy</label>
        <select
          value={filters.occupancy}
          onChange={(e) => setFilters(f => ({ ...f, occupancy: e.target.value }))}
        >
          <option value="">Any</option>
          <option value="Family">Family</option>
          <option value="Professional">Professional</option>
          <option value="Student">Student</option>
          <option value="Everyone">Everyone</option>
        </select>
      </div>
      <div className="filter-group">
        <label>Room Type</label>
        <select
          value={filters.roomType}
          onChange={(e) => setFilters(f => ({ ...f, roomType: e.target.value }))}
        >
          <option value="">Any</option>
          <option value="1BHK">1BHK</option>
          <option value="2BHK">2BHK</option>
          <option value="3BHK">3BHK</option>
          <option value="4BHK">4BHK</option>
          <option value="5BHK">5BHK</option>
        </select>
      </div>
      <div className="filter-group">
        <label>City</label>
        <select
          value={filters.city}
          onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}
        >
          <option value="">All (Assam)</option>
          <option value="Jorhat">Jorhat</option>
          <option value="Dergaon">Dergaon</option>
          <option value="Golaghat">Golaghat</option>
        </select>
      </div>
      <div className="filter-actions">
        <button className="apply-btn" onClick={onApply}>Apply</button>
        <button className="reset-btn" onClick={onReset}>Reset</button>
      </div>
    </div>
  );
}

// Get user ID utility
const getUserId = () => {
  let uid = localStorage.getItem("apnaghar_uid");
  if (!uid) {
    uid = "guest_" + Math.random().toString(36).substring(2, 10);
    localStorage.setItem("apnaghar_uid", uid);
  }
  return uid;
};

// Rent Section
function RentSection({ title, data, routeType, navigate, handleCardClick }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const sliderRef = useRef(null);

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const scrollLeft = sliderRef.current.scrollLeft;
    const cardWidth = sliderRef.current.firstChild?.offsetWidth + 12 || 0;
    const index = Math.round(scrollLeft / cardWidth);
    setCurrentIndex(index);
    if (!hasScrolled) setHasScrolled(true);
  };

  return (
    <div className="pg-section-block">
      <div className="section-header">
        <h2>{title}</h2>
        <button className="show-more-btn" onClick={() => navigate(`/rent-list-view/${routeType}`)}>Show More →</button>
      </div>
      <div className="pg-slider-wrapper">
        {!hasScrolled && data.length > 1 && (
          <div className="swipe-hint">
            <FaHandPointer className="hand-icon animated-hand" /> Swipe →
          </div>
        )}
        <div className="pg-slider" ref={sliderRef} onScroll={handleScroll}>
          {data.map(pg => {
            const isBooked = pg.showBooked;
            const isPremium = pg.isPremium;
            const isVerified = pg.isVerified;

            return (
              <div
                key={pg.id}
                className={`pg-card-wrapper ${isPremium ? 'premium-card-wrapper' : ''}`}
                onClick={() => !isBooked && handleCardClick(pg)}
                style={{ cursor: isBooked ? 'not-allowed' : 'pointer', opacity: isBooked ? 0.6 : 1 }}
              >
                {isBooked && (
                  <div className="booked-overlay">
                    <span className="booked-text">🏠 Booked</span>
                    <span className="booked-subtext">No rooms available</span>
                  </div>
                )}
                {isPremium && (
                  <div className="premium-badge">
                    <FaMedal className="badge-icons-pglisitngs" />
                    <span className="badge-text">Featured</span>
                  </div>
                )}
                <div className={`pg-card ${isPremium ? 'premium-card' : ''} ${isVerified && !isPremium ? 'verified-border' : ''}`}>
                  <img
                    src={pg.thumbnailURL || 'https://dummyimage.com/400x300/cccccc/000000&text=No+Image'}
                    alt={pg.pgName || 'Rent Image'}
                    className="pg-img"
                  />
                  <div className="pg-content">
                    <h3>{pg.pgName}{isVerified && <FaCheckCircle className="verified-tick" />}</h3>
                    <p><FaMapMarkerAlt className="icon" /> {pg.location || 'Assam'}</p>
                    <p><FaUserFriends className="icon" /> {pg.occupancy || 'N/A'}</p>
                    <p><FaBed className="icon" /> {pg.roomType || 'N/A'}</p>
                    <p className="price">₹ {pg.rent || 'N/A'}/month</p>
                    <p className="listing-id">Listing ID: <span>{pg.id}</span></p>

                    {pg.roadList && pg.roadList.length > 0 && (
                      <div className="road-slider">
                        {pg.roadList.map((r, i) => (
                          <div key={i} className="road-info">
                            <FaMapMarkerAlt className="road-icon" />
                            <span>{r.name}{r.distance ? ` • ${r.distance}` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <p className="rating" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {[1,2,3,4,5].map(n => (
                        <FaStar key={n} className="star" color={n <= Math.round(pg.ratingAvg || 0) ? '#f5c518' : '#d0d0d0'} />
                      ))}
                      <span className="rating-avg">{pg.ratingCount > 0 ? pg.ratingAvg.toFixed(1) : 'No ratings'}</span>
                      <span className="rating-count" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaEye />
                        <span>{pg.views || 0}</span>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="slider-dots">
          {data.map((_, i) => (
            <span key={i} className={`dot ${i === currentIndex ? 'active' : ''}`}></span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Rent Listing
function RentListing() {
  const [pgList, setPgList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState({
    minRent: "", maxRent: "", occupancy: "", roomType: "", city: ""
  });

  const navigate = useNavigate();

const [showSignInModal, setShowSignInModal] = useState(false);
// If you want a default image when no thumbnail is available


  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'rent-listings'), async (snapshot) => {
      setLoading(true);

      const dataWithDetails = await Promise.all(snapshot.docs.map(async docSnap => {
        const pg = { id: docSnap.id, ...docSnap.data() };
        let thumbnailURL = '';
        let location = 'Assam';
        let ratingAvg = 0;
        let ratingCount = 0;
        let nearestRoad = pg.nearestRoad || "Unknown Road";
        let roadList = pg.nearbyRoads || [];

        if (pg.thumbnailPath) {
          try { thumbnailURL = await getDownloadURL(ref(storage, pg.thumbnailPath)); } catch (e) { /* ignore */ }
        }

        if (pg.city) location = `${pg.city}, ${pg.state || 'Assam'}`;
        else if (pg.state) location = pg.state;

        try {
          const ratingsSnap = await getDocs(collection(db, 'rent-listings', pg.id, 'ratings'));
          let sum = 0, count = 0;
          ratingsSnap.forEach(r => {
            const val = r.data()?.value;
            if (typeof val === 'number') { sum += val; count += 1; }
          });
          ratingAvg = count ? sum / count : 0;
          ratingCount = count;
        } catch (err) { }

        return {
          ...pg,
          thumbnailURL,
          location,
          nearestRoad,
          roadList,
          ratingAvg,
          ratingCount,
          isVacant: pg.isVacant ?? true,
          showBooked: pg.showBooked ?? false,
          views: pg.views || 0,
          viewedBy: pg.viewedBy || []
        };
      }));

      setPgList(dataWithDetails);
      setLoading(false);
    });

    return () => unsub();
  }, []);

const handleCardClick = async (pg) => {
  try {
    const userId = localStorage.getItem("apnaghar_uid");
    if (!userId || userId.startsWith("guest_")) {
      // User not signed in
      setShowSignInModal(true);
      return; // Stop navigation
    }

    // Increment views if not already viewed
    if (!pg.viewedBy?.includes(userId)) {
      const pgRef = doc(db, "rent-listings", pg.id);
      await updateDoc(pgRef, { 
        views: increment(1), 
        viewedBy: arrayUnion(userId) 
      });
    }

    navigate(`/rent/${pg.id}`);
  } catch (err) { 
    console.error(err); 
  }
};

  const filteredPGs = useMemo(() => {
    return pgList.filter(pg => {
      return (
        (!filters.city || pg.location?.toLowerCase().includes(filters.city.toLowerCase())) &&
        (!filters.occupancy || pg.occupancy === filters.occupancy) &&
        (!filters.roomType || pg.roomType === filters.roomType) &&
        (!filters.minRent || pg.rent >= Number(filters.minRent)) &&
        (!filters.maxRent || pg.rent <= Number(filters.maxRent))
      );
    });
  }, [pgList, filters]);

  const premiumPGs = filteredPGs.filter(pg => pg.isPremium);
  const verifiedPGs = filteredPGs.filter(pg => pg.isVerified && !pg.isPremium);
const defaultImage = apnagharLogo;

// Dynamic SEO based on filtered or first listing
const seoTitle = "Explore Rent Listings Across Assam | ApkaGhr";
const seoDescription = "Find premium, verified, and budget-friendly rent listings in Assam. ApkaGhr provides direct listings from verified property owners with detailed info, nearby locations, and instant booking.";
// Use first listing's image if available
const seoImage = filteredPGs[0]?.thumbnailURL || defaultImage;
const seoUrl = window.location.href;
  return (
    <div className="pg-section">
      <LoadingScreen loading={loading} />
<Helmet>
  <title>{seoTitle}</title>
  <meta name="description" content={seoDescription} />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href={seoUrl} />

  {/* Open Graph / Social Meta */}
  <meta property="og:title" content={seoTitle} />
  <meta property="og:description" content={seoDescription} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content={seoUrl} />
  <meta property="og:image" content={seoImage} />

  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={seoTitle} />
  <meta name="twitter:description" content={seoDescription} />
  <meta name="twitter:image" content={seoImage} />

  {/* Structured Data */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Rent Listings in Assam",
      "url": seoUrl,
      "itemListElement": filteredPGs.map((pg, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `${window.location.origin}/rent/${pg.id}`,
        "name": pg.pgName,
        "image": pg.thumbnailURL || defaultImage,
        "description": `Rent in ${pg.location} - ${pg.roomType}, ${pg.occupancy}, ₹${pg.rent}/month`,
      }))
    })}
  </script>
</Helmet>


      <div className="pg-container" style={{ display: loading ? 'none' : 'block' }}>
        <div className="pg-brand-header">
          <img src={apnagharLogo} alt="ApkaGhr Logo" className="pg-logo" />
          <h1 className="pg-brand-name">ApkaGhr</h1>
        </div>

        <h2 className="pg-heading">
          <FaBuilding className="pg-heading-icon" /> Explore Rent Listings Across Assam
        </h2>

        <div className="broker-section">
          <div className="broker-content">
            <h2 className="broker-title">
              <FaShieldAlt className="broker-icon" /> 100% Broker & Commission Free
            </h2>
            <p className="broker-subtitle">
              <FaUserCheck className="broker-sub-icon" /> All listings are directly from verified property owners. No intermediaries, no hidden charges.
            </p>
          </div>
        </div>

        <button onClick={() => navigate('/homepage')} className="back-btnwhy">← Back</button>

        <div className="search-wrapper">
          <button className="filter-btn-only" onClick={() => setShowFilter(!showFilter)}>
            <FaFilter /> Filter
          </button>
        </div>

        {showFilter && (
          <FilterDropdown
            filters={filters}
            setFilters={setFilters}
            onApply={() => {
              setShowFilter(false);
              navigate('/rent-search-results', { state: { filteredRents: filteredPGs } });
            }}
            onReset={() => setFilters({ minRent: "", maxRent: "", occupancy: "", roomType: "", city: "" })}
            onClose={() => setShowFilter(false)}
          />
        )}

{showSignInModal && (
  <div className="modal-overlay">
    <div className="modal-content">
      <button className="modal-close-btn" onClick={() => setShowSignInModal(false)}>×</button>
      <h3>Sign In Required</h3>
      <p>Please sign in to view this property.</p>
      <div className="modal-actions">
        <button onClick={() => navigate('/signin')} className="sign-in-btn">Sign In</button>
      </div>
    </div>
  </div>
)}

        <RentSection title={<><Gem /> Premium Listings</>} data={premiumPGs.slice(0,6)} routeType="featured" navigate={navigate} handleCardClick={handleCardClick} />
        <RentSection title={<><ShieldCheck /> Verified Listings</>} data={verifiedPGs.slice(0,6)} routeType="verified" navigate={navigate} handleCardClick={handleCardClick} />
        <RentSection title={<><Compass /> All Rent Listings</>} data={filteredPGs.slice(0,6)} routeType="basic" navigate={navigate} handleCardClick={handleCardClick} />
      </div>
    </div>
  );
}

export default RentListing;
